/**
 * LEB-Abrechnung: was die Telekom uns schuldet und was sie gezahlt hat.
 *
 * ORIGINALTEXT MARVIN (22.09.2026)
 *   „ich will das wir die gegen das leb setzen und markieren das leb, das es
 *    abgerechnet wurde. So kann ich dann in einem weiteren Punkt prüfen
 *    Welche lebs noch nicht abgerechnet wurden und der Telekom eine Liste
 *    senden. Das geht aus unserem System besser als in der evgergabe."
 *   und auf die Rückfrage:
 *   „Hier geht es um sensible Informationen die nur ich sehen darf."
 *
 * ----------------------------------------------------------------------------
 * DER GANZE BILDSCHIRM LIEGT HINTER EINEM RECHT
 * ----------------------------------------------------------------------------
 * `abrechnung.geld` („Zahlen sehen"), dasselbe, das seit dem 21.09.2026 die
 * Rechnungsbeträge der Nachunternehmer trägt und allein bei „admin" liegt.
 * Hier steht kein Feld, das ohne dieses Recht zu sehen wäre - deshalb hängt
 * es am MENÜPUNKT und nicht an einzelnen Spalten. **Ein Bildschirm, auf dem
 * die Hälfte geschwärzt ist, verrät durch die Löcher, wie viele es sind.**
 *
 * ----------------------------------------------------------------------------
 * 5 ZUSTÄNDE UND NICHT 2
 * ----------------------------------------------------------------------------
 * „Abgerechnet oder nicht" wäre zu wenig. Der Fall, der Geld kostet, ist die
 * Gutschrift, die DA ist und zu niedrig ausfällt - die fällt in einer
 * Ja-Nein-Liste nie auf. Deshalb steht „zu wenig" ganz oben, vor den offenen.
 */

import { useEffect, useRef, useState } from 'react';
import { api, darf, datum, euro, tagIso, type Anmelder } from '../api.ts';
import { Kopf, Kachel } from '../teile/Geruest.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { Suchfeld, SuchHinweis, useVerzoegert } from '../teile/Schluesselsuche.tsx';
import { NummernKopieren, Prueffenster, RechnungChip } from '../teile/Rechnungspruefung.tsx';
import { voll } from '../teile/Abrechnungsfreigabe.tsx';
import { fragen, eingeben } from '../teile/rueckfrage.tsx';

/* DIE FARBE IST DIE DER SACHE UND KEINE ZIER - wie überall in diesem Haus.
   Was uns Geld kostet, trägt das Rot der Überfälligen; was erledigt ist, das
   Grün. „Offen" ist KEIN Fehler: ein LEB von gestern ist noch nicht
   abgerechnet, und das ist der Normalfall. */
const FARBE: Record<string, string> = {
  zu_wenig: 'z-ueberfaellig',
  offen: 'z-laufend',
  unklar: 'z-unterbrochen',
  /* GRAU UND NICHT ROT. Bei „ohne unseren Wert" ist von hier aus NICHTS zu
     tun - eine Warnfarbe an einer Zeile, an der niemand etwas ändern kann,
     ist ein Alarm ohne Knopf. */
  ohne_wert: 'z-laufend',
  zu_viel: 'z-unterbrochen',
  abgerechnet: 'z-erledigt',
};

const KURZ: Record<string, string> = {
  zu_wenig: 'Zu wenig',
  offen: 'Offen',
  unklar: 'Unklar',
  ohne_wert: 'LEB ohne Wert',
  zu_viel: 'Zu viel',
  abgerechnet: 'Abgerechnet',
};

/* WORAN ES LIEGT, WENN KEIN WERT DASTEHT.
 *
 * ORIGINALTEXT MARVIN (22.09.2026)
 *   „wie gehe ich nun damit um? hier scheint ein fehler in der darstellung
 *    sein."
 *
 * Er sah einen Strich und daneben „unklar" - und nirgends einen Grund.
 * **Ein Strich ohne Grund ist keine Auskunft, sondern eine Frage.** */
const GRUND: Record<string, string> = {
  ohne_positionen: 'keine Positionen gespeichert',
  ohne_preis: 'kein Preis im Rahmenvertrag',
  teils_ohne_preis: 'nicht jede Position hat einen Preis',
};

/* DIE REIHENFOLGE DER KNÖPFE IST DIE DER DRINGLICHKEIT und nicht das
   Alphabet: was Geld kostet, steht links. */
const REIHE = ['zu_wenig', 'offen', 'unklar', 'ohne_wert', 'zu_viel',
               'abgerechnet'];

/* WAS AUS DER GUTSCHRIFT FÜR DIE FIRMA FOLGT. Die Gutschrift ist die Annahme
   unseres LEB; die nächste Handlung ist die Freigabe zur Abrechnung. */
function folge(z: any): { text: string; farbe: string } {
  if (!z.auftrag_id) return { text: 'keinem Auftrag zugeordnet', farbe: 'z-unterbrochen' };
  if (z.bereit > 0) return { text: `bereit zur Freigabe (${z.bereit})`, farbe: 'z-neu' };
  if (z.freigegeben > 0) return { text: 'Firma freigegeben', farbe: 'z-erledigt' };
  return { text: 'weitere LEB noch offen', farbe: 'z-laufend' };
}

/**
 * NEUE GUTSCHRIFTEN (23.09.2026)
 *
 * ORIGINALTEXT MARVIN
 *   „wo sehe ich zb die gutschriften die heute nacht gekommen sind?"
 *   „ich kann hier bei den neen gutschriften ja garnichts einsehen, freigeben
 *    zum versenden als info an den partner bzw bereinigen"
 *   „das datum von der gutschrift und dem eingang muss vorne stehen und
 *    größer sein, affälliger. denn daf+r sortieren wir. ... Was meinstd u mit
 *    "ohne unseren wert"? Du kannst das dokument doch auslegen ... die
 *    auflisteung ist mir noch zu unübersichtlichtlich, das macht so keinen
 *    spass zu prüfen. passe es an"
 *
 * DER AUFBAU (.214):
 *   • Gegliedert nach dem DATUM DER GUTSCHRIFT, je Tag eine Überschrift mit
 *     Anzahl und Nettosumme.
 *   • Je Gutschrift vorn groß ihr Datum, darunter klein der Eingang.
 *   • Daneben Anschrift, Nummern und rechts der Nettobetrag mit dem URTEIL
 *     der Prüfung. Das Urteil kommt aus den Positionen der Gutschrift, Zeile
 *     für Zeile gegen Rahmenvertrag und unsere Menge (Migration 322). Es
 *     ersetzt „Ohne unseren Wert": das hiess nur, dass unser LEB aus der
 *     Übernahme keine Positionen hat.
 *   • Die Positionen klappen auf; bei einer Abweichung sind sie schon offen.
 *   • Darunter die Firmen mit Haken zum Freigeben, wie in .212.
 */
const FREIGABE_SICHT: [string, string][] = [
  ['alle', 'alle'], ['bereit', 'bereit zur Freigabe'],
  ['frei', 'freigegeben'], ['offen', 'weitere LEB offen'],
];
const URTEIL: Record<string, { kurz: string; farbe: string }> = {
  zu_wenig: { kurz: 'Zu wenig', farbe: 'z-ueberfaellig' },
  zu_viel: { kurz: 'Über unserem Wert', farbe: 'z-unterbrochen' },
  teils_offen: { kurz: 'Teilweise geprüft', farbe: 'z-laufend' },
  nicht_gelesen: { kurz: 'Nicht gelesen', farbe: 'z-unterbrochen' },
  passt: { kurz: 'Passt', farbe: 'z-erledigt' },
};
const URTEIL_REIHE = ['zu_wenig', 'zu_viel', 'teils_offen', 'nicht_gelesen', 'passt'];
const ZEILE_TEXT: Record<string, string> = {
  passt: 'passt',
  preis_niedriger: 'Preis unter Rahmenvertrag',
  preis_hoeher: 'Preis über Rahmenvertrag',
  menge_weniger: 'weniger als von uns gemeldet',
  menge_mehr: 'mehr als von uns gemeldet',
  nicht_im_lv: 'Leistung nicht im LV gefunden',
  ohne_rv_preis: 'kein Preis im Rahmenvertrag',
  ohne_unsere_menge: 'von uns nicht gemeldet',
  gebuehr: 'Gebühr, weitergegeben, nicht geprüft',
};
const ZEILE_ROT = new Set(['preis_niedriger', 'menge_weniger']);
const ZEILE_GELB = new Set(['preis_hoeher', 'menge_mehr']);

const menge = (w: unknown) => w === null || w === undefined ? '-'
  : Number(w).toLocaleString('de-DE', { maximumFractionDigits: 3 });

function Positionen({ z }: { z: any }) {
  const pos: any[] = z.positionen ?? [];
  const fehlt: any[] = z.fehlt ?? [];
  const quelle = pos.find((p) => p.unsere_quelle)?.unsere_quelle;
  return (
    <div className="gs-positionen">
      <table className="tabelle gs-tabelle">
        <thead>
          <tr>
            <th>Leistung</th>
            <th className="zahl">Telekom gutgeschrieben</th>
            <th className="zahl">{quelle === 'leb' ? 'von uns gemeldet (LEB)' : quelle === 'aufmass' ? 'unser Aufmaß (noch keine LEB)' : 'von uns'}</th>
            <th className="zahl">Einzelpreis</th>
            <th className="zahl">Rahmenvertrag</th>
            <th className="zahl">Gesamt</th>
            <th>Prüfung</th>
          </tr>
        </thead>
        <tbody>
          {pos.map((p) => {
            const u: string[] = z.zeilen_urteil?.[p.nr] ?? [];
            const rot = u.some((x) => ZEILE_ROT.has(x));
            const gelb = !rot && u.some((x) => ZEILE_GELB.has(x));
            return (
              <tr key={p.nr} className={rot ? 'gs-rot' : gelb ? 'gs-gelb' : u.includes('gebuehr') ? 'gs-gebuehr' : ''}>
                <td>
                  {p.text}
                  {p.oz && <span className="klein leise">, {p.oz}</span>}
                </td>
                <td className="zahl">
                  {menge(p.menge)} {p.einheit}
                  {/* ZEILE FÜR ZEILE (.251). Marvin: „du musst hier nichts
                      zusammenaddieren, weil es im LEB auch einzelne positionen
                      sind." Nur gegen das Aufmaß wird noch je Leistung
                      zusammengezählt, dort steht die Summe dabei. */}
                  {p.unsere_quelle === 'aufmass' && p.zeilen_gleicher_leistung > 1 && (
                    <div className="klein" title="Dieselbe Leistung steht in dieser Bestellung mehrfach.">
                      zusammen {menge(p.menge_telekom)}
                    </div>
                  )}
                </td>
                <td className="zahl">{u.includes('gebuehr') ? <span className="leise">{menge(p.menge_unsere)}</span> : menge(p.menge_unsere)}</td>
                <td className="zahl">{euro(p.einzelpreis)}</td>
                <td className="zahl">{p.rv_preis != null ? euro(p.rv_preis) : '-'}</td>
                <td className="zahl">{euro(p.gesamt)}</td>
                <td className="klein">
                  {u.map((x) => ZEILE_TEXT[x] ?? x).join(', ')}
                  {p.unsere_quelle === 'aufmass' && p.zeilen_gleicher_leistung > 1 && u.includes('menge_mehr') ? ' (mehrfach gutgeschrieben)' : ''}
                </td>
              </tr>
            );
          })}
          {fehlt.map((x, i) => (
            <tr key={'f' + x.oz + '_' + i} className={x.oz === '10099980' ? 'gs-gebuehr' : 'gs-rot'}>
              <td>{x.text ?? 'Leistung'} <span className="klein leise">, {x.oz}</span></td>
              <td className="zahl">-</td>
              <td className="zahl">{menge(x.menge)}</td>
              <td className="zahl">-</td><td className="zahl">-</td><td className="zahl">-</td>
              <td className="klein">{x.oz === '10099980' ? 'Gebühr, nicht in der Gutschrift (nicht geprüft)' : 'fehlt in der Gutschrift'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!quelle && (
        <p className="klein leise" style={{ margin: '4px 0 0' }}>
          Bei uns liegt zu dieser Bestellung weder ein LEB mit Positionen noch ein Aufmaß.
          Geprüft sind nur die Preise gegen den Rahmenvertrag.
        </p>
      )}
    </div>
  );
}

function NeueGutschriften({ ich, oeffne, gehZu }: {
  ich: Anmelder; oeffne: (id: string, reiter?: string) => void; gehZu?: (s: string) => void;
}) {
  const [tage, setTage] = useState(7);
  const [sicht, setSicht] = useState('alle');
  const [pruefung, setPruefung] = useState('alle');
  /* ZU ERLEDIGEN / ERLEDIGT (.309). Marvin: „warum verschwinden die
     gutschriften nicht in eine kategorie, wenn ich sie geprüft habe, und sie
     freigegeben habe? … ich nicht weiß was jetzt erledigt wurde oder nicht." */
  const [ansicht, setAnsicht] = useState<'zu' | 'erledigt' | 'alle'>('zu');
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set());
  const [laeuft, setLaeuft] = useState(false);
  const [offen, setOffen] = useState<Record<string, boolean>>({});
  const [pruefe, setPruefe] = useState<string | null>(null);
  const darfFreigeben = darf(ich, 'abrechnung.freigeben');
  /* EINMAL 90 TAGE, DIE TAGE WÄHLT DER BILDSCHIRM (.230).
     Marvin: „läuft a. viel zu langsam, und reagiert es bei klicks nicht
     immer". Jeder Klick auf „7 Tage" fragte den Server neu, und kam die
     Antwort des vorigen Klicks später an, überschrieb sie die neue. Jetzt
     wird einmal geladen, und die Knöpfe filtern sofort. Der Zähler `lauf`
     wirft eine überholte Antwort weg. */
  const lauf = useRef(0);
  async function laden() {
    const n = ++lauf.current;
    try {
      const neu = await api.neueGutschriften(90);
      if (n === lauf.current) { setD(neu); setFehler(null); }
    } catch (f: any) { if (n === lauf.current) setFehler(String(f?.message ?? f)); }
  }
  useEffect(() => { laden(); }, []);
  useEffect(() => { setGewaehlt(new Set()); }, [tage]);
  useFrisch(laden);
  /* NACH DEM DATUM DER GUTSCHRIFT, in deutscher Zeit: „7 Tage" heißt ab
     heute vor 7 Tagen, 0 Uhr. */
  const abTag = tagIso(new Date(Date.now() - tage * 86400000));
  const firmen = (z: any): any[] => Array.isArray(z.freigaben) ? z.freigaben : [];
  /* ERLEDIGT: an einem Auftrag, keine Firma wartet mehr auf die Freigabe, und
     die Prüfung ist abgeschlossen (alles passt, oder von Hand abgehakt). */
  const wartet = (z: any) => firmen(z).filter((f: any) => !f.freigabe_id).length;
  const geprueftFertig = (z: any) => z.urteil === 'passt' || !!z.geprueft_am;
  const erledigt = (z: any) => !!z.auftrag_id && wartet(z) === 0 && geprueftFertig(z);
  const nochZuTun = (z: any): string[] => [
    !z.auftrag_id ? 'Auftrag zuordnen' : '',
    wartet(z) ? `Freigabe (${wartet(z)} ${wartet(z) === 1 ? 'Firma' : 'Firmen'})` : '',
    !geprueftFertig(z) ? `Prüfung abschließen (${(URTEIL[z.urteil] ?? URTEIL.nicht_gelesen).kurz})` : '',
  ].filter(Boolean);
  const geladen90: any[] = d?.zeilen ?? [];
  const zahlZu = geladen90.filter((z) => !erledigt(z)).length;
  /* „Zu erledigen" zeigt ALLES Offene der 90 Tage, der Zeitfilter gilt dort nicht. */
  const alleZeilen: any[] = ansicht === 'zu'
    ? geladen90.filter((z) => !erledigt(z))
    : geladen90.filter((z: any) => String(z.tag ?? '') >= abTag).filter((z) => ansicht === 'alle' || erledigt(z));
  const zahlErledigt = geladen90.filter((z: any) => String(z.tag ?? '') >= abTag && erledigt(z)).length;
  async function abhaken(z: any, zurueck: boolean) {
    let notiz: string | null = null;
    if (!zurueck && z.urteil !== 'passt') {
      notiz = await eingeben('Prüfung abschließen. Was geschieht mit der Abweichung? (z. B. „bei der Telekom reklamiert", „ist so richtig")', '');
      if (notiz === null) return;
    }
    setLaeuft(true); setFehler(null);
    try { await api.gutschriftGeprueft(z.id, zurueck, notiz); setHinweis(zurueck ? 'Wieder offen.' : 'Erledigt.'); await laden(); }
    catch (f: any) { setFehler(String(f?.message ?? f)); }
    finally { setLaeuft(false); }
  }
  const art = (z: any) => !z.auftrag_id ? 'ohne'
    : z.bereit > 0 ? 'bereit' : z.freigegeben > 0 ? 'frei' : 'offen';
  const zahlF = (s: string) => s === 'alle' ? alleZeilen.length
    : alleZeilen.filter((z) => art(z) === s).length;
  const zahlU = (u: string) => alleZeilen.filter((z) => z.urteil === u).length;
  const zeilen = alleZeilen
    .filter((z) => sicht === 'alle' || art(z) === sicht)
    .filter((z) => pruefung === 'alle' || z.urteil === pruefung);
  const key = (z: any, f: any) => `${z.auftrag_id}|${f.subunternehmer_id}|${f.gewerk_gruppe}`;

  /* ALLE BEREITEN EINTRÄGE, EINMAL JE AUFTRAG, FIRMA UND GEWERK. */
  const bereite = new Map<string, any>();
  for (const z of alleZeilen) {
    for (const f of firmen(z)) if (!f.freigabe_id) bereite.set(key(z, f), { z, f });
  }
  const berlin = (t: string, was: Intl.DateTimeFormatOptions) =>
    new Date(t).toLocaleString('de-DE', { timeZone: 'Europe/Berlin', ...was });

  /* DIE TAGE, IN DER REIHENFOLGE DER LISTE (der Server sortiert nach dem Datum der Gutschrift). */
  const tagesliste: { tag: string; zeilen: any[] }[] = [];
  for (const z of zeilen) {
    const letzter = tagesliste[tagesliste.length - 1];
    if (letzter && letzter.tag === z.tag) letzter.zeilen.push(z);
    else tagesliste.push({ tag: z.tag, zeilen: [z] });
  }
  const tagKopf = (tag: string) => {
    const t = new Date(tag + 'T12:00:00Z');
    return `${t.toLocaleDateString('de-DE', { weekday: 'long', timeZone: 'Europe/Berlin' })}, ${datum(tag)}`;
  };

  function schalte(k: string) {
    const n = new Set(gewaehlt);
    if (n.has(k)) n.delete(k); else n.add(k);
    setGewaehlt(n);
  }
  function waehle(welche: 'bereit' | 'rechnung' | 'keine') {
    if (welche === 'keine') { setGewaehlt(new Set()); return; }
    const n = new Set<string>();
    for (const [k, { f }] of bereite) if (welche === 'bereit' || f.rechnung_da) n.add(k);
    setGewaehlt(n);
  }

  async function los(schonFreigegeben: boolean) {
    const eintraege = [...gewaehlt].filter((k) => bereite.has(k)).map((k) => {
      const { z, f } = bereite.get(k);
      return { auftrag_id: z.auftrag_id, subunternehmer_id: f.subunternehmer_id,
               gewerk_gruppe: f.gewerk_gruppe };
    });
    if (!eintraege.length) return;
    const nFirmen = new Set(eintraege.map((e) => e.subunternehmer_id)).size;
    if (!await fragen(schonFreigegeben
      ? `${eintraege.length} als schon freigegeben festhalten (über den alten Weg `
        + 'schon hinausgegangen)? Die Firmen bekommen KEINE Nachricht.'
      : `${eintraege.length} zur Abrechnung freigeben? ${nFirmen} `
        + `${nFirmen === 1 ? 'Firma bekommt' : 'Firmen bekommen'} eine Nachricht in Glocke `
        + 'und Telegram, ohne Positionen und Mengen.')) return;
    setLaeuft(true); setFehler(null); setHinweis(null);
    try {
      const e = await api.freigeben(eintraege, schonFreigegeben);
      setHinweis(e.klartext);
      setGewaehlt(new Set());
      await laden();
    } catch (f: any) { setFehler(String(f?.message ?? f)); }
    finally { setLaeuft(false); }
  }

  async function nachlesen(id?: string) {
    setLaeuft(true); setFehler(null); setHinweis(id ? null : 'Alle Gutschriften werden aus ihrer Datei neu gelesen …');
    try { const e = await api.gutschriftenNachlesen(id); setHinweis(e.klartext); await laden(); }
    catch (f: any) { setFehler(String(f?.message ?? f)); }
    finally { setLaeuft(false); }
  }

  const stand = (f: any): { text: string; farbe: string } => {
    if (f.freigabe_id) {
      return f.wie === 'bestand'
        ? { text: 'erledigt, ohne Nachricht', farbe: 'z-erledigt' }
        : { text: `freigegeben am ${datum(f.freigegeben_am)}`, farbe: 'z-erledigt' };
    }
    return { text: 'bereit zur Freigabe', farbe: 'z-neu' };
  };
  const n = [...gewaehlt].filter((k) => bereite.has(k)).length;
  const mitRechnung = [...bereite.values()].filter(({ f }) => f.rechnung_da).length;
  /* WORAN ES ÜBER ALLE GUTSCHRIFTEN LIEGT (.215). Marvin: „was heißt "nicht
     ganz prüfbar"?" Die Gründe je Position, zusammengezählt: steht dort bei
     300 Positionen „kein Preis im Rahmenvertrag", ist es eine Lücke in den
     Stammdaten und kein Fehler der Telekom. */
  const gruende: Record<string, number> = {};
  for (const z of alleZeilen) {
    for (const u of Object.values(z.zeilen_urteil ?? {}) as string[][]) {
      for (const x of u) {
        if (x === 'nicht_im_lv' || x === 'ohne_rv_preis' || x === 'ohne_unsere_menge') {
          gruende[x] = (gruende[x] ?? 0) + 1;
        }
      }
    }
  }
  /* OFFEN IST, WAS DER MENSCH AUFGEKLAPPT HAT, SONST WAS EINE ABWEICHUNG HAT. */
  const istOffen = (z: any) => offen[z.id] ?? (z.urteil === 'zu_wenig' || z.urteil === 'zu_viel');

  return (
    <div className="karte neuegutschriften">
      <div className="listenkopf" style={{ marginBottom: 8 }}>
        <div className="knopfreihe gs-reiter">
          <button className={'knopf' + (ansicht === 'zu' ? ' haupt' : '')} aria-pressed={ansicht === 'zu'} onClick={() => setAnsicht('zu')}>
            Zu erledigen ({zahlZu})
          </button>
          <button className={'knopf' + (ansicht === 'erledigt' ? ' haupt' : '')} aria-pressed={ansicht === 'erledigt'} onClick={() => setAnsicht('erledigt')}>
            Erledigt ({zahlErledigt})
          </button>
          <button className={'knopf' + (ansicht === 'alle' ? ' haupt' : '')} aria-pressed={ansicht === 'alle'} onClick={() => setAnsicht('alle')}>
            Alle
          </button>
        </div>
        <div className="knopfreihe">
          {ansicht !== 'zu' && [[0, 'heute'], [1, 'seit gestern'], [7, '7 Tage'], [30, '30 Tage']].map(([t, w]) => (
            <button key={t} className={'knopf klein' + (tage === t ? ' gewaehlt' : '')} aria-pressed={tage === t}
                    onClick={() => setTage(t as number)}>{w}</button>
          ))}
          <button className="knopf klein" disabled={laeuft} onClick={() => nachlesen()}
                  title="Alle Gutschriften aus ihrer abgelegten Datei neu lesen: Betrag, Datum, Bestellung und Positionen.">
            Neu auslesen
          </button>
        </div>
      </div>

      {alleZeilen.length > 0 && (
        <div className="gs-filter">
          <div className="knopfreihe">
            <span className="klein leise gs-filtername">Prüfung</span>
            <button className={'knopf klein' + (pruefung === 'alle' ? ' gewaehlt' : '')}
                    onClick={() => setPruefung('alle')}>alle</button>
            {URTEIL_REIHE.filter((u) => zahlU(u) > 0).map((u) => (
              <button key={u} className={'knopf klein' + (pruefung === u ? ' gewaehlt' : '')} aria-pressed={pruefung === u}
                      onClick={() => setPruefung(u)}>{URTEIL[u].kurz} ({zahlU(u)})</button>
            ))}
          </div>
          <div className="knopfreihe">
            <span className="klein leise gs-filtername">Freigabe</span>
            {FREIGABE_SICHT.map(([s, w]) => (
              <button key={s} className={'knopf klein' + (sicht === s ? ' gewaehlt' : '')} aria-pressed={sicht === s}
                      onClick={() => setSicht(s)}>{w} ({zahlF(s)})</button>
            ))}
          </div>
        </div>
      )}

      {Object.keys(gruende).length > 0 && (
        <div className="hinweis gs-gruende klein">
          <b>Warum nicht jede Position geprüft ist:</b>{' '}
          {Object.entries(gruende).map(([u, n]) => `${ZEILE_TEXT[u]} bei ${n} ${n === 1 ? 'Position' : 'Positionen'}`).join(', ')}.
          {gruende.ohne_rv_preis ? ' Fehlt der Preis, fehlt er im Rahmenvertrag unter Stammdaten.' : ''}
          {gruende.nicht_im_lv ? ' Nicht im LV heißt: der Text der Telekom passt zu keiner Leistung in unserem Katalog.' : ''}
          {gruende.ohne_unsere_menge ? ' Keine Menge heißt: weder LEB mit Positionen noch Aufmaß.' : ''}
        </div>
      )}

      {darfFreigeben && bereite.size > 0 && (
        <div className="knopfreihe gutschrift-aktionen" style={{ margin: '8px 0' }}>
          <button className="knopf klein" onClick={() => waehle('bereit')}>
            alle bereiten wählen ({bereite.size})
          </button>
          {mitRechnung > 0 && (
            <button className="knopf klein" onClick={() => waehle('rechnung')}
                    title="Die Firma hat schon eine Rechnung geschickt: sie wusste es also schon.">
              alle mit Rechnung wählen ({mitRechnung})
            </button>
          )}
          {n > 0 && <button className="knopf klein" onClick={() => waehle('keine')}>keine</button>}
          <button className="knopf haupt klein" disabled={!n || laeuft} onClick={() => los(false)}
                  title="Die Firma bekommt die Info, dass sie abrechnen kann (Glocke, Menü „Freigaben“, Telegram), ohne Positionen und Mengen.">
            Freigeben ({n})
          </button>
          {/* KLARER BENANNT (.241). Marvin: „Wie kann ich denn hier eine
              freigabe bereits als erledigt markieren, zb. brester hat die
              rechnung schon geschrieben, dann brauche ich ihm ja die
              mitteilung nihct mehr zu senden." Genau das tat „Schon
              freigegeben" - der Name sagte es nicht. */}
          <button className="knopf klein" disabled={!n || laeuft} onClick={() => los(true)}
                  title="Als erledigt festhalten, OHNE Nachricht an die Firma - etwa wenn sie die Rechnung schon geschrieben hat.">
            Erledigt, ohne Nachricht ({n})
          </button>
        </div>
      )}
      {hinweis && <div className="hinweis erledigt">{hinweis}</div>}
      {fehler && <div className="hinweis fehler">{fehler}</div>}
      {!d && !fehler && <p className="klein leise" style={{ margin: 0 }}>Wird geladen …</p>}
      {d && !alleZeilen.length && (
        <p className="klein leise" style={{ margin: 0 }}>{ansicht === 'zu' ? 'Nichts zu erledigen: jede Gutschrift ist geprüft und freigegeben.' : 'In diesem Zeitraum steht hier keine Gutschrift.'}</p>
      )}
      {d && alleZeilen.length > 0 && !zeilen.length && (
        <p className="klein leise" style={{ margin: 0 }}>In dieser Auswahl steht nichts.</p>
      )}

      {tagesliste.map(({ tag, zeilen: zt }) => {
        const summe = zt.reduce((s, z) => s + (Number(z.betrag_netto) || 0), 0);
        return (
          <section key={tag} className="gs-tag">
            <h3 className="gs-tagkopf">
              <span>{tagKopf(tag)}</span>
              <span className="klein leise">
                {zt.length} {zt.length === 1 ? 'Gutschrift' : 'Gutschriften'}, netto {euro(summe)}
              </span>
            </h3>
            {zt.map((z) => {
              const u = URTEIL[z.urteil] ?? URTEIL.nicht_gelesen;
              const fs = firmen(z);
              const pos: any[] = z.positionen ?? [];
              return (
                <article key={z.id} className={'gs-karte gs-' + z.urteil}>
                  <div className="gs-datum">
                    <div className="gs-datum-gross">
                      {z.datum ? datum(z.datum).slice(0, 6) : '??.??.'}
                    </div>
                    <div className="klein leise">{z.datum ? 'Gutschrift' : 'Datum nicht gelesen'}</div>
                    <div className="gs-eingang">
                      Eingang {berlin(z.eingegangen_am, { day: '2-digit', month: '2-digit' })}
                      {' '}{berlin(z.eingegangen_am, { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>

                  <div className="gs-mitte">
                    <div className="gs-titel">
                      {z.auftrag_id ? (
                        <button className="alslink" onClick={() => oeffne(z.auftrag_id)}>
                          {voll(z) || 'ohne Anschrift'}
                        </button>
                      ) : <span>Keinem Auftrag zugeordnet, Bestellung {z.belegnummer ?? '?'}</span>}
                    </div>
                    <div className="gs-nummern klein">
                      <NummernKopieren kls={z.kls_id} sm={z.sm_nummer} />
                      <span className="leise">
                        Nr. {z.nummer}
                        {z.belegnummer ? `, Bestellung ${z.belegnummer}` : ''}
                        {z.lz_von ? `, Leistung ${datum(z.lz_von)} bis ${datum(z.lz_bis)}` : ''}
                        {z.weitere_am_beleg > 0 ? `, ${z.weitere_am_beleg} weitere Gutschrift zu dieser Bestellung` : ''}
                      </span>
                    </div>
                    {(z.urteil === 'nicht_gelesen' || z.betrag_netto == null) && z.gelesen_befund && (
                      <div className="klein gs-befund">{z.gelesen_befund}</div>
                    )}
                    <div className="knopfreihe gs-knoepfe">
                      {z.datei_da && (
                        <a className="knopf klein" target="_blank" rel="noreferrer"
                           href={api.gutschriftAdresse(z.id, z.dateiname)}>Gutschrift ansehen</a>
                      )}
                      {pos.length > 0 && (
                        <button className="knopf klein" onClick={() => setOffen({ ...offen, [z.id]: !istOffen(z) })}>
                          {istOffen(z) ? 'Positionen zu' : `Positionen (${pos.length})`}
                        </button>
                      )}
                      {z.datei_da && (z.urteil === 'nicht_gelesen' || z.betrag_netto == null) && (
                        <button className="knopf klein" disabled={laeuft} onClick={() => nachlesen(z.id)}>
                          Neu lesen
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="gs-rechts">
                    <div className="gs-betrag">{z.betrag_netto != null ? euro(z.betrag_netto) : '-'}</div>
                    <div className="klein leise">netto</div>
                    <span className={'marke-zustand ' + u.farbe} title={z.urteil_text}>{u.kurz}</span>
                    <div className="klein leise gs-urteiltext">{z.urteil_text}</div>
                  </div>

                  {/* WAS NOCH FEHLT, ODER: ERLEDIGT (.309). */}
                  <div className="gs-voll gs-erledigung klein">
                    {erledigt(z) ? (
                      <>
                        <span className="marke-zustand z-erledigt">erledigt</span>
                        {z.geprueft_am && <span className="leise"> Prüfung abgeschlossen {z.geprueft_name ? `von ${z.geprueft_name} ` : ''}am {datum(z.geprueft_am)}{z.geprueft_notiz ? `: ${z.geprueft_notiz}` : ''}</span>}
                        {z.geprueft_am && <button className="knopf klein" disabled={laeuft} onClick={() => abhaken(z, true)}>wieder öffnen</button>}
                      </>
                    ) : (
                      <>
                        <b>Noch zu tun:</b> {nochZuTun(z).join(', ')}
                        {!geprueftFertig(z) && (
                          <button className="knopf klein" disabled={laeuft} onClick={() => abhaken(z, false)}>Prüfung abgeschlossen</button>
                        )}
                        {z.geprueft_am && <span className="leise"> (Prüfung abgeschlossen{z.geprueft_notiz ? `: ${z.geprueft_notiz}` : ''})</span>}
                      </>
                    )}
                  </div>

                  {istOffen(z) && pos.length > 0 && <div className="gs-voll"><Positionen z={z} /></div>}

                  {fs.length > 0 && (
                    <div className="gs-voll gutschrift-firmen">
                      {fs.map((x) => {
                        const k = key(z, x);
                        const s = stand(x);
                        return (
                          <label key={k} className="gutschrift-firma klein">
                            {!x.freigabe_id && darfFreigeben && (
                              <input type="checkbox" checked={gewaehlt.has(k)} onChange={() => schalte(k)} />
                            )}
                            <span>{x.firma}, {x.gewerk_gruppe === 'tiefbau' ? 'Tiefbau' : 'Montage'}</span>
                            <span className={'marke-zustand ' + s.farbe}>{s.text}</span>
                            {/* DIE RECHNUNG MIT IHREM ERGEBNIS (.215): „ob die
                                rechnungen stimmen und gegengehalten worden mit
                                den pistionen". Ein Klick öffnet das Prüffenster. */}
                            {x.rechnung ? <RechnungChip r={x.rechnung} pruefen={setPruefe} />
                              : x.rechnung_da && (
                              <span className="marke-zustand z-laufend"
                                    title="Die Firma hat zu diesem Auftrag schon eine Rechnung geschickt.">
                                Rechnung {x.rechnung_nummer ?? 'liegt vor'}
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                  {!fs.length && z.auftrag_id && (
                    <div className="gs-voll klein leise">{folge(z).text}</div>
                  )}
                </article>
              );
            })}
          </section>
        );
      })}

      {gehZu && (
        <div style={{ marginTop: 8 }}>
          <button className="knopf klein" onClick={() => gehZu('rechnungen')}>
            Alle Freigaben in „Rechnungen prüfen"
          </button>
        </div>
      )}
      {pruefe && (
        <Prueffenster ich={ich} id={pruefe} zu={() => setPruefe(null)} fertig={() => laden()} />
      )}
    </div>
  );
}

export default function LebAbrechnung({
  ich, oeffne, gehZu,
}: { ich: Anmelder; oeffne: (id: string, reiter?: string) => void;
     gehZu?: (s: string) => void }) {
  const [zustand, setZustand] = useState('offen');
  const [monat, setMonat] = useState('');
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  /* DIE SUCHE (.300): KLS-ID, SM, Anschrift, Belegnummer, Gutschrift - in
     ALLEN Zuständen, auch abgerechnet. */
  const [suche, setSuche] = useState('');
  const sucheV = useVerzoegert(suche);

  async function laden() {
    try {
      setD(await api.lebAbrechnung({ zustand, monat: monat || undefined, suche: sucheV || undefined }));
      setFehler(null);
    } catch (f: any) { setFehler(String(f?.message ?? f)); }
  }
  useEffect(() => { laden(); }, [zustand, monat, sucheV]);
  useFrisch(laden);

  /* OHNE DAS RECHT GIBT ES DIESEN BILDSCHIRM NICHT.
     Der Menüpunkt hängt am Recht, und dies ist der Riegel dahinter: wer die
     Adresse von Hand eintippt, bekommt denselben Satz und keine halbe
     Tabelle. */
  if (!darf(ich, 'abrechnung.geld')) {
    return (
      <>
        <Kopf titel="LEB-Abrechnung" weg="" />
        <div className="inhalt">
          <div className="hinweis warnung">
            Dieser Bildschirm zeigt Beträge und verlangt das Recht „Zahlen
            sehen".
          </div>
        </div>
      </>
    );
  }

  const zahl = (z: string) =>
    (d?.zahlen ?? []).find((x: any) => x.zustand === z)?.n ?? 0;
  const wert = (z: string) =>
    (d?.zahlen ?? []).find((x: any) => x.zustand === z)?.wert ?? null;

  return (
    <>
      <Kopf
        titel="LEB-Abrechnung"
        weg="Was wir bei der Telekom eingereicht haben, gegen das, was sie
             gutgeschrieben hat" />
      <div className="inhalt">
        {fehler && <div className="hinweis fehler">{fehler}</div>}

        <NeueGutschriften ich={ich} oeffne={oeffne} gehZu={gehZu} />

        {d && (
          <div className="kachelreihe">
            {/* DIE ZAHL, AUF DIE ES ANKOMMT, STEHT LINKS. Nicht „wie viele
                sind offen" - das ist der Normalfall -, sondern „wo fehlt
                Geld". */}
            <Kachel wert={zahl('zu_wenig')} was="zu wenig gutgeschrieben"
                    farbe={zahl('zu_wenig') > 0
                             ? 'var(--ueberfaellig)' : undefined} />
            <Kachel wert={zahl('offen')} was="noch nicht abgerechnet" />
            {/* DIE KACHEL ZEIGT DEN FALL, IN DEM ETWAS ZU TUN IST.
                „ohne unseren Wert" steht bewusst NICHT als Kachel daneben:
                dort ist nichts zu tun, und eine Kachel ist eine
                Aufforderung. Der Knopf darunter führt trotzdem hin. */}
            <Kachel wert={zahl('unklar')} was="Betrag nachzutragen" />
            <Kachel wert={zahl('abgerechnet')} was="abgerechnet" />
          </div>
        )}

        <p className="klein leise">
          Gerechnet wird je <b>Bestellung</b> und nicht je Datei: eine
          Gutschrift der Telekom nennt die Bestellung, und zu einer
          Bestellung können ein Teilaufmaß und ein Schlussaufmaß gehören.
          Der Wert kommt aus Menge mal Einheitspreis des Rahmenvertrags am
          Tag der Abgabe. Fehlt ein Preis, bleibt der Wert leer statt 0.
          {zahl('ohne_wert') > 0 && (
            <>
              {' '}<b>Bei {zahl('ohne_wert')} Bestellungen fehlt unser
              eigener Wert</b> - zu diesen LEB-Dateien ist keine Position
              gespeichert, weil sie aus der Zeit vor der Umstellung stammen
              und nur als Tatsache übernommen wurden. Dort ist von hier aus
              nichts zu tun; der Betrag der Telekom steht trotzdem in der
              Zeile.
            </>
          )}
          {wert('zu_wenig') !== null && Number(wert('zu_wenig')) > 0 && (
            <>
              {' '}<b>In „zu wenig" stehen {euro(wert('zu_wenig'))} an
              eingereichtem Wert.</b>
            </>
          )}
        </p>

        {/* ---------------------------------------------------- Die Filter */}
        <div className="listenkopf">
          <div className="knopfreihe">
            {REIHE.map((z) => (
              <button key={z}
                      className={'knopf' + (zustand === z ? ' gewaehlt' : '')}
                      onClick={() => setZustand(z)}>
                {KURZ[z]} {d ? `(${zahl(z)})` : ''}
              </button>
            ))}
            <button className={'knopf' + (zustand === 'alle' ? ' gewaehlt' : '')}
                    onClick={() => setZustand('alle')}>Alle</button>
          </div>
          <Suchfeld wert={suche} setze={setSuche} mehr="Belegnr., Gutschrift" />
          <select value={monat} onChange={(e) => setMonat(e.target.value)}>
            <option value="">Alle Monate</option>
            {(d?.monate ?? []).map((m: string) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

        {/* ------------------------------------------------- Die Ausfuhr */}
        <div className="wegknoepfe" style={{ marginBottom: 12 }}>
          {/* SIE NIMMT DEN GEWÄHLTEN ZUSTAND MIT. Ein Knopf, der immer
              dasselbe ausführt, egal was auf dem Bildschirm steht, ist ein
              Knopf, dem niemand glaubt. */}
          <a className="knopf haupt"
             href={api.lebAusfuhrPfad({ zustand, monat: monat || undefined })}
             download>
            Excel für die Telekom ({KURZ[zustand] ?? 'Alle'}
            {monat ? `, ${monat}` : ''})
          </a>
          {/* MIT WERTEN IST EIN EIGENER KNOPF UND KEIN HAKEN.
              Was wir erwarten, ist eine Verhandlungsposition und keine
              Auskunft - die Datei trägt „MIT_WERTEN" im Namen, damit sie
              niemand aus Versehen anhängt. */}
          <a className="knopf"
             href={api.lebAusfuhrPfad({ zustand, monat: monat || undefined,
                                        mitWerten: true })}
             download
             title="Nur für den eigenen Gebrauch: die Datei trägt „MIT_WERTEN“
                    im Namen">
            … mit unseren Werten
          </a>
        </div>

        {/* --------------------------------------------------- Die Liste */}
        {d && <SuchHinweis suche={sucheV} n={d.zeilen.length} />}
        {d && d.zeilen.length === 0 && (
          <div className="hinweis">
            Hier steht nichts. {sucheV.trim() ? 'Kein Beleg passt zur Suche.' : zustand === 'offen'
              ? 'Zu jeder eingereichten LEB-Datei liegt eine Gutschrift.'
              : 'Kein Beleg in diesem Zustand.'}
          </div>
        )}

        {d && d.zeilen.length > 0 && (
          <table className="liste">
            <thead>
              <tr>
                <th>Stand</th>
                <th>Bestellung</th>
                <th>Art</th>
                <th>Eingereicht</th>
                <th>Baustelle</th>
                <th style={{ textAlign: 'right' }}>LEB-Wert</th>
                <th style={{ textAlign: 'right' }}>Gutschrift</th>
                <th style={{ textAlign: 'right' }}>Differenz</th>
              </tr>
            </thead>
            <tbody>
              {d.zeilen.map((z: any) => (
                <tr key={z.leb_export_id}
                    style={{ cursor: 'pointer' }}
                    title="Auftragsakte öffnen"
                    /* „leb" UND NICHT „abrechnung": diesen Reiter gibt es
                       in der Akte nicht, und ein unbekannter Schlüssel
                       liess die Akte LEER. Von der LEB-Abrechnung kommt
                       man, also geht es auch dorthin. */
                    onClick={() => oeffne(z.auftrag_id, 'leb')}>
                  <td className="nichtumbrechen">
                    <span className={'marke-zustand ' + (FARBE[z.zustand] ?? '')}
                          title={d.zustaende[z.zustand] ?? ''}>
                      {KURZ[z.zustand] ?? z.zustand}
                    </span>
                  </td>
                  <td className="nichtumbrechen">
                    {z.belegnummer}
                    {/* DIE ZWEITZEILE IST EIN BLOCK UND KEIN `span`.
                        Beim 1. Bild klebte „4214229047Pos. 00010"
                        aneinander: `.weg` steht im Kopf der Seite als
                        eigene Zeile, in einer Tabellenzelle nicht. Die
                        Arbeitsliste benutzt dafür `div.klein.leise` -
                        dieselbe Klasse, damit beide Listen gleich
                        aussehen. */}
                    <div className="klein leise">Pos. {z.belegposition}</div>
                    {/* WIE VIELE LEB-DATEIEN AN DIESER BESTELLUNG HÄNGEN.
                        Ohne die Zahl sieht eine Bestellung mit Teil- und
                        Schlussaufmaß aus wie eine doppelte Zeile. */}
                    {z.lebs_am_beleg > 1 && (
                      <div className="klein leise">
                        {z.lebs_am_beleg} Dateien an dieser Bestellung
                      </div>
                    )}
                  </td>
                  <td className="nichtumbrechen">
                    {z.art === 'teil' ? 'Teilaufmaß' : 'Schlussaufmaß'}
                  </td>
                  <td className="nichtumbrechen">
                    {datum(z.eingereicht_am)}
                    {z.gutschrift_zuletzt && (
                      <div className="klein leise">
                        Gutschrift {datum(z.gutschrift_zuletzt)}
                      </div>
                    )}
                  </td>
                  <td>
                    {z.strasse} {z.hausnummer}{z.hausnummer_zusatz ?? ''}
                    <div className="klein leise">
                      {z.plz} {z.ort_text}, KLS {z.kls_id ?? '-'}{z.sm_nummer ? `, SM ${z.sm_nummer}` : ''}
                    </div>
                  </td>
                  <td style={{ textAlign: 'right' }}
                      className="nichtumbrechen">
                    {z.leb_wert !== null ? euro(z.leb_wert) : '-'}
                    {/* POSITIONEN OHNE PREIS SAGEN ES. Eine Summe, der 3
                        von 20 Positionen fehlen, ist eine zu kleine Zahl -
                        und eine zu kleine Zahl sieht aus wie eine Zahl. */}
                    {z.ohne_preis > 0 && (
                      <div className="klein"
                           style={{ color: 'var(--ueberfaellig)' }}>
                        {z.ohne_preis} ohne Preis
                      </div>
                    )}
                    {/* UND WENN GAR KEIN WERT DASTEHT, WARUM.
                        Hier stand ein nackter Strich, und daneben „unklar".
                        Beides zusammen sagt nichts. */}
                    {z.leb_wert === null && z.wert_grund
                      && !(z.ohne_preis > 0) && (
                      <div className="klein leise">
                        {GRUND[z.wert_grund] ?? z.wert_grund}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}
                      className="nichtumbrechen">
                    {z.gutschrift_betrag !== null
                      ? euro(z.gutschrift_betrag) : '-'}
                    {z.gutschriften > 0 && (
                      <div className="klein leise"
                           title={z.gutschrift_nummern ?? ''}>
                        {z.gutschriften === 1
                          ? z.gutschrift_nummern
                          : `${z.gutschriften} Gutschriften`}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}
                      className="nichtumbrechen">
                    {z.differenz !== null ? (
                      <b style={{ color: Number(z.differenz) > 0
                                    ? 'var(--ueberfaellig)' : undefined }}>
                        {euro(z.differenz)}
                      </b>
                    ) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
